/**
 * The live screen.
 *
 * The one the product is actually about, and the one that changes shape with distance:
 *
 *   above 30 m   the arrow — a trustworthy bearing, confidently drawn
 *   below 30 m   the hot/cold ring, because relative GPS error is now comparable to the gap
 *   below 15 m   the handoff: "look around and wave", plus the beacon
 *   15 m for 10s converged
 *
 * No compass anywhere in that? The north-up face instead of the arrow. Anchor rather than
 * walker? The calm panel instead of all of it.
 *
 * In a room of more than two, one person gets the arrow and everybody else gets a chevron on the
 * rim and a row in the roll call. Which person that is defaults to the nearest — or the leader,
 * if somebody is leading — and can be changed with one tap.
 */

import {
  CONFIDENCE_LABEL,
  ConvergenceWatcher,
  compassPoint,
  formatDistance,
  normalizeDegrees,
  rangeModeFor,
  type ParticipantId,
  type RallyPoint,
  type RangeMode,
  type Role,
  type SignalKind,
} from '@beeline/shared';

import { ArrivalAlert } from '../../lib/alerts';
import { ClosingTracker, describeClosing } from '../../lib/closing';
import { el } from '../../lib/dom';
import {
  bearingUncertaintyDegrees,
  describeClimb,
  describeGhost,
  geometryFor,
  geometryForAll,
  ghostRadiusMetres,
  type Geometry,
} from '../../lib/geometry';
import { haptics } from '../../lib/haptics';
import {
  defaultFocus,
  otherMembers,
  selfRole,
  type MemberState,
  type SessionState,
  type Staleness,
} from '../../lib/session-model';
import { createArrowScene, type ArrowScene } from '../../scene/arrow-scene';
import { createNorthUpView, type NorthUpView } from '../../scene/north-up';
import { createProximityView, type ProximityView } from '../../scene/proximity';
import type { SensorState } from '../../sensors';
import { createBeacon } from '../../ui/beacon';
import { createDebugPanel, onTripleTap, type DebugRow } from '../../ui/debug-panel';
import { PocketGuide } from '../../lib/guidance';
import { createHandsFree, type HandsFree } from '../../ui/hands-free';
import { createPeek, peekEntriesFrom, type Peek } from '../../ui/peek';
import { createRim, type Rim } from '../../ui/rim';
import { createSignals, type Signals } from '../../ui/signals';
import { createRollCall, type RollCall } from '../../ui/roll-call';
import { createTrackPlot, type TrackPlot } from '../../ui/track-plot';
import { Track } from '../../lib/track';
import { anchorPanel, roleQuestion, type AnchorPanel } from './anchor';

export interface LiveScreenOptions {
  code: string;
  /**
   * A fixed destination rather than a person: a pinned spot or a guided-arrival link.
   *
   * The whole experience is reused unchanged — the arrow, the ranges, the beacon — with two
   * differences that would otherwise be lies. There is nobody to anchor, and a pin has no
   * signal, so it can never go stale.
   */
  solo?: boolean;
  /**
   * A picture of what to look for, shown only at the handoff range.
   *
   * That range is where the product stops navigating and starts helping you look, and a photo
   * of the gate is worth more there than any amount of bearing. Above it, it would just be
   * something covering the arrow.
   */
  handoffPhoto?: string | null;
  onChooseRole?: (role: Role) => void;
  /** Open the map picker to set or move the room's rally point. */
  onChooseRally?: () => void;
  /** Clear it again, putting every arrow back on a person. */
  onClearRally?: () => void;
  /** Send a wave, or one of the four fixed phrases, to the rest of the room. */
  onSignal?: (signal: SignalKind) => void;
  /** Fired once, when this device has been inside the handoff range long enough. */
  onConverged: () => void;
}

/**
 * What the arrow is pointing at.
 *
 * A person and a place are different things, and every screen below this cares about exactly
 * three of those differences: a place has no signal so it can never go stale, a place has no
 * role, and a place cannot decide it has arrived. Flattening both into one shape here means the
 * rest of the screen never asks which it is dealing with.
 */
export interface FocusTarget {
  id: ParticipantId | 'rally';
  name: string;
  position: { lat: number; lng: number; altitude: number | null } | null;
  staleness: Staleness;
  present: boolean;
  photo: string | null;
}

const targetForMember = (member: MemberState): FocusTarget => ({
  id: member.id,
  name: member.name ?? 'Them',
  position: member.frame
    ? { lat: member.frame.lat, lng: member.frame.lng, altitude: member.frame.alt ?? null }
    : null,
  staleness: member.staleness,
  present: member.present,
  photo: null,
});

const targetForRally = (rally: RallyPoint): FocusTarget => ({
  id: 'rally',
  name: rally.label,
  position: { lat: rally.lat, lng: rally.lng, altitude: null },
  // A place has no signal, so it is exactly as fresh now as when it was dropped. Saying
  // otherwise would be noise dressed up as honesty.
  staleness: 'live',
  present: true,
  photo: rally.photo ?? null,
});

export interface LiveScreen {
  element: HTMLElement;
  /** Everything the screen shows is derived from these two. */
  update: (session: SessionState, sensors: SensorState) => void;
  /**
   * Show a signal that just arrived.
   *
   * Pushed rather than derived from state, because a wave is an *event*: it happens once, it is
   * not a property of the room, and nothing about it should survive being seen.
   */
  onSignal: (signal: SignalKind, from: string | null) => void;
  destroy: () => void;
}

export type { Geometry } from '../../lib/geometry';

const STALENESS_TEXT = (staleness: Staleness, name: string | null): string | null => {
  switch (staleness) {
    case 'lost':
      return `Waiting for ${name ? `${name}'s` : 'their'} signal`;
    case 'stale':
      return `${name ? `${name}'s` : 'Their'} last position is a few seconds old`;
    default:
      return null;
  }
};

/**
 * The words under the big number.
 *
 * Ordered by what would be dishonest to leave out: a dead connection first, then a missing or
 * unusable fix, then a stale one, and only after all of that anything encouraging.
 */
export function statusLine(
  session: SessionState,
  sensors: SensorState,
  geometry: Geometry,
  mode: RangeMode = 'arrow',
  focus: FocusTarget | null = null,
): string {
  if (session.connection === 'reconnecting') return 'Reconnecting…';
  if (!focus) return 'Waiting for them to connect';
  if (!focus.present && !focus.position) return 'Waiting for them to connect';

  if (!sensors.fix) return 'Looking for your position…';
  if (!sensors.fix.usable) return 'Your signal is too weak to point with';
  if (!focus.position) return 'Waiting for their first position';

  const stale = STALENESS_TEXT(focus.staleness, focus.id === 'rally' ? null : focus.name);
  if (stale) return stale;

  // The last few metres are a human problem, not a navigation one.
  if (mode === 'handoff') return 'You are basically there — look around and wave';
  if (mode === 'hotcold') return 'Close now — walk slowly and watch the pulse';

  // A big vertical gap is usually *the* problem — on a slope or a stadium tier, "180 m" plus a
  // bearing sends someone confidently along the wrong terrace.
  if (geometry.climbMetres !== null) {
    return `${describeClimb(geometry.climbMetres)} — find a way up or down first`;
  }

  if (!sensors.headingAvailable && geometry.bearing !== null) {
    // No compass: say the direction in words, which stays genuinely usable.
    return `${compassPoint(geometry.bearing)} — hold the phone flat and walk`;
  }

  if (geometry.onCourse) return 'On course — keep going';
  return 'Turn until the arrow points up';
}

export function liveScreen(options: LiveScreenOptions): LiveScreen {
  let wasOnCourse = false;
  /** Passed back into `rangeModeFor` so the thresholds are sticky rather than flickering. */
  let mode: RangeMode = 'arrow';

  const convergence = new ConvergenceWatcher();
  let convergedReported = false;

  /**
   * Null means "whoever `defaultFocus` picks"; anything else means the user tapped a chevron or
   * a roll-call row, and their choice outranks every heuristic below it until they change it.
   */
  let chosenFocus: ParticipantId | 'rally' | null = null;

  /**
   * How wide the honest answer is, drawn behind the arrow.
   *
   * A needle says "that way". A needle inside a wedge says "that way, roughly this roughly" —
   * the same fact, told in a way that survives being wrong. It is a DOM element rather than
   * scene geometry so that the north-up fallback and the arrow can share it.
   */
  const cone = el('div', { class: 'live__cone', 'aria-hidden': 'true', hidden: true });

  const stage = el('div', { class: 'live__stage' });

  /** The warmer/colder trend, which is what actually works once the bearing has stopped. */
  const closing = new ClosingTracker();
  let closingOf: ParticipantId | 'rally' | null = null;

  /* --- putting the phone away ------------------------------------------- */

  const alert = new ArrivalAlert();
  const pocket = new PocketGuide();

  const signals: Signals = createSignals({
    onSend: (signal) => options.onSignal?.(signal),
  });

  const handsFree: HandsFree = createHandsFree({
    onAlertRange: (range) => alert.set(range),
    onPocketMode: (on) => {
      if (on) pocket.start();
      else pocket.stop();
    },
  });

  /**
   * The three stage occupants are mutually exclusive and each is built only when it is needed:
   * a phone with no compass never pays for a WebGL context, and nothing renders an arrow while
   * the ring is up.
   */
  let arrow: ArrowScene | null = null;
  let northUp: NorthUpView | null = null;
  let proximity: ProximityView | null = null;

  const clearStage = (keep: 'arrow' | 'northup' | 'proximity'): void => {
    if (keep !== 'arrow' && arrow) {
      arrow.destroy();
      arrow = null;
    }
    if (keep !== 'northup' && northUp) {
      northUp.destroy();
      northUp = null;
    }
    if (keep !== 'proximity' && proximity) {
      proximity.destroy();
      proximity = null;
    }
  };

  const showArrow = (): ArrowScene => {
    clearStage('arrow');
    if (!arrow) {
      arrow = createArrowScene();
      stage.replaceChildren(arrow.canvas, cone, rim.element);
    }
    return arrow;
  };

  const showNorthUp = (): NorthUpView => {
    clearStage('northup');
    if (!northUp) {
      northUp = createNorthUpView();
      stage.replaceChildren(northUp.element, cone);
    }
    return northUp;
  };

  const showProximity = (): ProximityView => {
    clearStage('proximity');
    if (!proximity) {
      proximity = createProximityView();
      stage.replaceChildren(proximity.element, rim.element);
    }
    return proximity;
  };

  const handoffPhoto = el('img', {
    class: 'live__photo',
    hidden: true,
    alt: 'What to look for',
  }) as HTMLImageElement;
  if (options.handoffPhoto) handoffPhoto.src = options.handoffPhoto;

  const distance = el('output', { class: 'live__distance mono', text: '—' });
  const peerLabel = el('p', { class: 'live__peer' });
  const status = el('p', { class: 'live__status', role: 'status', 'aria-live': 'polite' });
  const confidence = el('span', { class: 'chip chip--stale', text: 'no signal yet' });

  /* --- the room --------------------------------------------------------- */

  const focusOn = (id: ParticipantId | 'rally'): void => {
    chosenFocus = id;
    if (lastRender) update(lastRender.session, lastRender.sensors);
  };

  const rim: Rim = createRim({ onFocus: focusOn });

  /**
   * Where this device has been, kept in memory for the life of the screen and nowhere else.
   *
   * It never reaches the socket, the telemetry summary or storage of any kind — it exists to be
   * drawn in the corner of the stage and to be forgotten when the screen goes.
   */
  const track = new Track();
  const trackPlot: TrackPlot = createTrackPlot();

  /** Hold the stage to see the shape of the group; let go and it is an arrow again. */
  const peek: Peek = createPeek();
  const unbindPeek = peek.bindTo(stage);
  const rollCall: RollCall = createRollCall({ onFocus: focusOn });

  const debug = createDebugPanel();
  /** Kept so opening a panel can fill it at once rather than waiting for the next fix. */
  let lastRender: { session: SessionState; sensors: SensorState } | null = null;

  const untap = onTripleTap(distance, () => {
    debug.toggle();
    if (lastRender) update(lastRender.session, lastRender.sensors);
  });

  /*
   * Re-evaluate every second on the last known state.
   *
   * Convergence is "fifteen metres for ten seconds" — a property of *time*, not of message
   * arrival. Without this it would only advance when a fix or a peer frame happened to turn up,
   * so two people standing still with a quiet GPS would never be told they had met. It also
   * keeps the signal-age readouts honest between updates.
   */
  const ticker = window.setInterval(() => {
    if (lastRender) update(lastRender.session, lastRender.sensors);
  }, 1000);

  /* --- the beacon ------------------------------------------------------- */

  const beacon = createBeacon((active) => {
    beaconButton.textContent = active ? 'Turn the beacon off' : 'Beacon';
    beaconButton.classList.toggle('button--primary', !active);
    beaconButton.classList.toggle('button--ghost', active);
  });

  const beaconButton = el(
    'button',
    { class: 'button button--primary button--small live__beacon', type: 'button', hidden: true },
    'Beacon',
  );
  beaconButton.addEventListener('click', () => beacon.toggle());

  /* --- the rally point -------------------------------------------------- */

  const rallyButton = el(
    'button',
    {
      class: 'button button--ghost button--small live__rally',
      type: 'button',
      hidden: true,
      'data-action': 'set-rally',
    },
    'Set a meeting point',
  );
  rallyButton.addEventListener('click', () => options.onChooseRally?.());

  /**
   * Take the meeting point away again.
   *
   * The counterpart to "anyone can move it" (`ProductOverview.md` §4.3b): plans change, and a
   * group that has abandoned the gate should not spend the rest of the session with every arrow
   * pointing at it. Only offered once there is one, and it puts every arrow back on a person.
   */
  const rallyClearButton = el(
    'button',
    {
      class: 'button button--ghost button--small live__rally-clear',
      type: 'button',
      hidden: true,
      'data-action': 'clear-rally',
    },
    'Drop it',
  );
  rallyClearButton.addEventListener('click', () => {
    // The arrow was almost certainly locked onto the rally point; hand it back to the heuristic
    // rather than leaving it pointing at something that no longer exists.
    if (chosenFocus === 'rally') chosenFocus = null;
    options.onClearRally?.();
  });

  /* --- leading ---------------------------------------------------------- */

  /**
   * The third choreography, after "one of you stays put" and "everyone walks to a place".
   *
   * Sometimes the answer is neither: a tour guide, a parent at a festival, whoever is at the
   * front of a hike. One person says they are leading and everybody else's arrow locks onto
   * them and stays there, however the group shuffles around. Advisory, like every role here —
   * it changes what the arrows default to, not what anybody is allowed to do.
   */
  const leadButton = el(
    'button',
    {
      class: 'button button--ghost button--small live__lead',
      type: 'button',
      hidden: true,
      'data-action': 'lead',
    },
    'Lead the group',
  );

  /** Kept off the render path so the button knows what it is toggling the instant it is tapped. */
  let leading = false;

  leadButton.addEventListener('click', () => {
    options.onChooseRole?.(leading ? 'walker' : 'leader');
  });

  /* --- the anchor protocol ---------------------------------------------- */

  const roles = el('div', { class: 'live__roles', hidden: true });
  let anchor: AnchorPanel | null = null;
  /** Asked once per session; declining is a real answer, not a "not yet". */
  let roleAsked = false;

  const readout = el(
    'div',
    { class: 'live__readout' },
    peerLabel,
    distance,
    status,
    handoffPhoto,
    el('div', { class: 'live__indicators' }, confidence, rollCall.toggle, handsFree.toggle),
    el('div', { class: 'live__actions' }, beaconButton, rallyButton, rallyClearButton, leadButton),
    signals.element,
  );

  /**
   * Everything the walking screen is made of, in order.
   *
   * A function rather than a literal because the anchor panel replaces the lot and then has to
   * put it all back. Two hand-written lists drift; one list cannot.
   */
  const composition = (): HTMLElement[] => [
    stage,
    // Outside the stage rather than in it, because the stage's children are swapped wholesale
    // every time the mode changes — and deliberately absent from the anchor panel below, where
    // somebody standing still has no track worth drawing.
    trackPlot.element,
    readout,
    roles,
    peek.element,
    rollCall.element,
    handsFree.element,
    debug.element,
    beacon.element,
  ];

  const element = el('main', { class: 'screen live' }, ...composition());

  const askForRole = (focus: FocusTarget | null): void => {
    if (roleAsked || roles.firstChild) return;
    roles.hidden = false;
    roles.replaceChildren(
      roleQuestion({
        peerName: focus?.name ?? null,
        onChoose: (role) => {
          roleAsked = true;
          roles.hidden = true;
          roles.replaceChildren();
          options.onChooseRole?.(role);
        },
        onSkip: () => {
          roleAsked = true;
          roles.hidden = true;
          roles.replaceChildren();
        },
      }),
    );
  };

  function update(session: SessionState, sensors: SensorState): void {
    lastRender = { session, sensors };

    const self = sensors.fix
      ? {
          lat: sensors.fix.lat,
          lng: sensors.fix.lng,
          usable: sensors.fix.usable,
          altitude: sensors.fix.altitude ?? null,
        }
      : null;

    // Offered every frame, kept only when it is movement rather than the receiver arguing with
    // itself. Nothing downstream of this ever leaves the device.
    if (sensors.fix) track.record(sensors.fix, Date.now());

    const others = otherMembers(session);
    const everyone = geometryForAll(self, others, sensors);

    /*
     * What the arrow is about, in order of who gets to decide:
     *
     *   1. whatever the user last tapped, if it still exists
     *   2. the rally point, because a room with a shared destination has one answer, not N
     *   3. the leader, then the nearest person
     */
    const picked =
      chosenFocus === 'rally'
        ? session.rally
          ? targetForRally(session.rally)
          : null
        : chosenFocus
          ? (() => {
              const member = others.find((entry) => entry.id === chosenFocus);
              return member ? targetForMember(member) : null;
            })()
          : null;

    const fallbackMember = defaultFocus(session, self);
    const focus: FocusTarget | null =
      picked ??
      (session.rally
        ? targetForRally(session.rally)
        : fallbackMember
          ? targetForMember(fallbackMember)
          : null);

    // Recomputed rather than read out of `everyone`, because only the one arrow actually being
    // drawn has a previous on-course state worth holding on to. The rim has no glow to strobe.
    const hero = geometryFor(self, focus?.position ?? null, sensors, wasOnCourse);
    wasOnCourse = hero.onCourse;

    // Both of these are per-target: switching from one person to another has to start again, or
    // the screen would report the previous target's approach as this one's — and "buzz me at
    // 100 m" would either fire the instant the arrow lands on somebody already close, or never
    // fire again because it already fired for somebody else.
    if (focus?.id !== closingOf) {
      closing.reset();
      alert.reset();
      closingOf = focus?.id ?? null;
    }
    closing.record(hero.distanceMetres, Date.now());

    // "Buzz me when they are close" and the spoken guidance both run off the same numbers the
    // screen is showing, so what you hear can never disagree with what you would have seen.
    alert.update(hero.distanceMetres, focus?.id === 'rally' ? focus.name : (focus?.name ?? null));
    pocket.update({
      distanceMetres: hero.distanceMetres,
      relative: hero.relative,
      bearing: hero.bearing,
      name: focus?.name ?? null,
    });

    const previousMode = mode;
    mode = rangeModeFor(hero.distanceMetres, mode);
    if (mode !== previousMode && mode !== 'arrow') haptics.modeChange();

    // Convergence is a duration, not an instant: one optimistic sample must not declare
    // success while two people are still looking around for each other.
    const now = Date.now();
    if (convergence.update(hero.distanceMetres, now) && !convergedReported) {
      convergedReported = true;
      haptics.arrival();
      options.onConverged();
    }

    const role = selfRole(session);
    rollCall.update(everyone, focus?.id ?? null, {
      rally: session.rally,
      rallyGeometry: geometryFor(
        self,
        session.rally ? { ...session.rally, altitude: null } : null,
        sensors,
      ),
    });

    /* --- the anchor's screen: no arrow, because an anchor has nowhere to go --- */

    if (role === 'anchor') {
      clearStage('proximity');
      proximity?.destroy();
      proximity = null;

      if (!anchor) {
        anchor = anchorPanel({
          peerName: focus?.id === 'rally' ? null : (focus?.name ?? null),
          onSwap: () => options.onChooseRole?.('walker'),
        });
        element.replaceChildren(
          anchor.element,
          rollCall.element,
          handsFree.element,
          debug.element,
          beacon.element,
        );
      }
      anchor.update({
        distanceMetres: hero.distanceMetres,
        // Which way they are coming *from*, which is the reverse of the way we would walk.
        bearingFromPeer: hero.bearing === null ? null : normalizeDegrees(hero.bearing),
      });
      debug.update(debugRows(options.code, session, sensors, hero, mode, focus));
      return;
    }

    if (anchor) {
      anchor.destroy();
      anchor = null;
      // The same children, in the same order, as the initial composition. Rebuilding it from a
      // hand-written list is how `peek.element` went missing after an anchor swapped to walking:
      // hold-to-peek still bound to the stage, but its overlay was no longer in the document.
      element.replaceChildren(...composition());
    }

    /* --- readouts --------------------------------------------------------- */

    // Who or what the number is about. In a group this is the difference between "220 m" being
    // useful and being a mystery, so it always names the thing rather than assuming.
    const focusedMember = others.find((member) => member.id === focus?.id);

    distance.textContent = hero.distanceMetres === null ? '—' : formatDistance(hero.distanceMetres);

    /*
     * A lost signal is not an absence, it is a circle that grows at walking pace. Saying
     * "somewhere within 180 m" is both more honest and more useful than a frozen arrow, because
     * it tells you the arrow has stopped being worth following and roughly when it stopped.
     */
    const lostFor =
      focusedMember && focusedMember.staleness === 'lost' && focusedMember.receivedAt !== null
        ? Date.now() - focusedMember.receivedAt
        : null;
    const ghost =
      lostFor === null
        ? null
        : describeGhost(
            focusedMember?.name ?? null,
            lostFor,
            ghostRadiusMetres(lostFor, focusedMember?.frame?.acc ?? null),
          );

    /*
     * Under thirty metres the bearing has stopped being trustworthy, but the *trend* has not:
     * both fixes wander, and they wander together. "12 m closer in the last 15 s" is something
     * a person can act on at a range where an arrow is not.
     */
    const trend = mode === 'arrow' ? null : describeClosing(closing.read());

    peerLabel.textContent = !focus
      ? others.length > 1
        ? 'Nearest'
        : 'Them'
      : focus.id === 'rally'
        ? `Meeting point · ${focus.name}`
        : focusedMember?.role === 'leader'
          ? `Following ${focus.name}`
          : focus.name;
    status.textContent = ghost ?? trend ?? statusLine(session, sensors, hero, mode, focus);
    status.classList.toggle('live__status--warn', ghost !== null);

    const grade = sensors.fix?.confidence ?? 'unusable';
    confidence.textContent = sensors.fix ? CONFIDENCE_LABEL[grade] : 'no signal yet';
    confidence.className = `chip chip--${grade === 'good' ? 'good' : grade === 'okay' ? 'okay' : 'poor'}`;

    // A pinned spot has no signal, so it can never be stale — saying otherwise would be noise.
    const stale = !options.solo && (focus?.staleness === 'stale' || focus?.staleness === 'lost');
    element.classList.toggle('live--on-course', hero.onCourse && mode === 'arrow');
    element.classList.toggle('live--stale', stale);
    element.classList.toggle('live--close', mode !== 'arrow');

    // The beacon and the photo are only offered where they help: at the range where the answer
    // has stopped being a bearing and started being a pair of eyes.
    beaconButton.hidden = mode !== 'handoff';
    if (mode !== 'handoff') beacon.stop();

    const photo = focus?.photo ?? options.handoffPhoto ?? null;
    if (photo && handoffPhoto.getAttribute('src') !== photo) handoffPhoto.src = photo;
    handoffPhoto.hidden = mode !== 'handoff' || !photo;

    // The roll call earns its place as soon as there is more than one thing to point at — which
    // a rally point makes true even in a pair.
    rollCall.toggle.hidden = others.length + (session.rally ? 1 : 0) < 2;

    // Nobody to signal to on a pinned spot, and nobody to signal to in an empty room.
    signals.element.hidden = options.solo === true || others.length === 0;

    // Setting a meeting point is only offered in a real session, and only where a map exists.
    rallyButton.hidden = options.solo === true || !options.onChooseRally;
    rallyButton.textContent = session.rally ? 'Move the meeting point' : 'Set a meeting point';
    rallyClearButton.hidden = options.solo === true || !options.onClearRally || !session.rally;

    // Leading is a group shape. In a pair the anchor protocol already asked the better question,
    // and offering both would be two ways to say the same thing badly.
    leading = role === 'leader';
    const someoneElseLeads = others.some((member) => member.role === 'leader');
    leadButton.hidden = options.solo === true || others.length < 2 || someoneElseLeads;
    leadButton.textContent = leading ? 'Stop leading' : 'Lead the group';
    leadButton.classList.toggle('live__lead--on', leading);

    /* --- the stage -------------------------------------------------------- */

    // The cone: the same bearing, drawn as wide as it actually is. It only makes sense while an
    // arrow is up — below thirty metres the product has already stopped pointing.
    const spread = bearingUncertaintyDegrees(
      hero.distanceMetres,
      sensors.fix?.accuracy ?? null,
      focusedMember?.frame?.acc ?? 0,
    );
    // Under about six degrees a wedge is indistinguishable from the needle and just adds glow.
    const showCone = mode === 'arrow' && spread !== null && spread >= 6 && hero.relative !== null;
    cone.hidden = !showCone;
    if (showCone) {
      cone.style.setProperty('--cone-angle', `${hero.relative ?? 0}deg`);
      cone.style.setProperty('--cone-spread', `${Math.min(90, spread ?? 0)}deg`);
    }

    if (mode === 'arrow' && sensors.headingAvailable) {
      showArrow().update({
        relative: hero.relative,
        heading: sensors.heading,
        onCourse: hero.onCourse,
        // A compass fighting a car park is not a signal worth drawing confidently either.
        stale: stale || sensors.headingSuspect,
      });
    } else if (mode === 'arrow') {
      // No compass: the north-up face, never a frozen arrow.
      showNorthUp().update(hero.bearing);
    } else {
      // Close in, bearing is not trustworthy — so stop drawing one and say why.
      showProximity().update(hero.distanceMetres, { haptic: !stale });
    }

    rim.update(
      everyone.filter((entry) => entry.member.id !== focus?.id),
      sensors.headingAvailable && mode !== 'handoff',
    );

    /*
     * The corner plot. The projection is only computed when there is something to draw and a
     * range at which drawing it is honest — a few hundred haversines a second is cheap, but not
     * so cheap it is worth spending on a plot nobody is looking at.
     */
    const plotting = track.length >= 3 && mode !== 'handoff' && sensors.fix !== null;
    trackPlot.update({
      points: plotting && sensors.fix ? track.offsetsFrom(sensors.fix, Date.now()) : [],
      spreadMetres: plotting && sensors.fix ? track.spreadFrom(sensors.fix) : 0,
      // No compass means north-up, exactly as everywhere else that draws an angle.
      heading: sensors.headingAvailable ? sensors.heading : null,
      handoff: mode === 'handoff',
    });

    peek.update(
      peekEntriesFrom(
        everyone,
        session.rally
          ? {
              label: session.rally.label,
              geometry: geometryFor(self, { ...session.rally, altitude: null }, sensors),
            }
          : null,
      ),
      !sensors.headingAvailable,
    );

    /* --- the one question ------------------------------------------------- */

    // Asked of whoever created the session, once somebody else is actually present and there is
    // a distance worth choosing over. Never in a group, where anchoring is the wrong shape, and
    // never while two people are already on top of each other.
    if (
      !options.solo &&
      options.onChooseRole &&
      session.you === session.members[0]?.id &&
      others.length === 1 &&
      role === null &&
      focus?.present &&
      mode === 'arrow' &&
      hero.distanceMetres !== null
    ) {
      askForRole(focus);
    }

    debug.update(debugRows(options.code, session, sensors, hero, mode, focus));
  }

  return {
    element,
    update,
    onSignal: (signal, from) => signals.receive(signal, from),
    destroy: () => {
      window.clearInterval(ticker);
      untap();
      debug.destroy();
      beacon.destroy();
      unbindPeek();
      peek.destroy();
      trackPlot.destroy();
      rim.destroy();
      rollCall.destroy();
      signals.destroy();
      handsFree.destroy();
      pocket.stop();
      anchor?.destroy();
      arrow?.destroy();
      northUp?.destroy();
      proximity?.destroy();
    },
  };
}

/** Everything worth knowing when the arrow is wrong and you are standing on a beach. */
function debugRows(
  code: string,
  session: SessionState,
  sensors: SensorState,
  geometry: Geometry,
  mode: RangeMode = 'arrow',
  focus: FocusTarget | null = null,
): DebugRow[] {
  const fix = sensors.fix;
  const target = focus?.position ?? null;
  const member =
    focus && focus.id !== 'rally'
      ? session.members.find((entry) => entry.id === focus.id)
      : undefined;
  const age = member?.receivedAt == null ? null : Date.now() - member.receivedAt;
  const six = (value: number): string => value.toFixed(6);

  return [
    { label: 'session', value: code },
    { label: 'you', value: session.you ?? '—' },
    { label: 'room', value: `${session.members.length}/${session.capacity}` },
    { label: 'socket', value: session.connection, warn: session.connection !== 'open' },
    { label: 'self', value: fix ? `${six(fix.lat)}, ${six(fix.lng)}` : '—' },
    {
      label: 'self acc',
      value: fix ? `${fix.accuracy.toFixed(1)} m (${fix.confidence})` : '—',
      warn: fix ? !fix.usable : true,
    },
    { label: 'self age', value: fix ? `${((Date.now() - fix.at) / 1000).toFixed(1)} s` : '—' },
    {
      label: 'heading',
      value: sensors.heading === null ? 'none' : `${sensors.heading.toFixed(1)}°`,
      warn: !sensors.headingAvailable,
    },
    {
      label: 'heading src',
      value: `${sensors.headingSource ?? 'none'}${sensors.headingAbsolute ? '' : ' (relative!)'}`,
      warn: sensors.headingAvailable && !sensors.headingAbsolute,
    },
    { label: 'focus', value: focus ? `${focus.id} ${focus.name}`.trim() : '—' },
    { label: 'rally', value: session.rally ? session.rally.label : 'none' },
    { label: 'target', value: target ? `${six(target.lat)}, ${six(target.lng)}` : '—' },
    { label: 'target acc', value: member?.frame ? `${member.frame.acc.toFixed(1)} m` : '—' },
    {
      label: 'target age',
      value: age === null ? '—' : `${(age / 1000).toFixed(1)} s`,
      warn: focus?.staleness === 'stale' || focus?.staleness === 'lost',
    },
    {
      label: 'bearing',
      value: geometry.bearing === null ? '—' : `${geometry.bearing.toFixed(1)}°`,
    },
    {
      label: 'relative',
      value: geometry.relative === null ? '—' : `${geometry.relative.toFixed(1)}°`,
    },
    {
      label: 'distance',
      value: geometry.distanceMetres === null ? '—' : `${geometry.distanceMetres.toFixed(1)} m`,
    },
    {
      label: 'climb',
      value: geometry.climbMetres === null ? '—' : `${geometry.climbMetres.toFixed(1)} m`,
    },
    { label: 'mode', value: mode },
    { label: 'role', value: selfRole(session) ?? 'none' },
    { label: 'simulated', value: sensors.simulated ? 'yes' : 'no', warn: sensors.simulated },
  ];
}
